import { Palette } from 'Palette';
import FundInvestmentInvestor from './admin/FundInvestmentData';
import Contact from './Contact';
import FundOpportunity from './FundOpportunity';
import { InvestmentEntityAllocation } from './InvestmentEntity';

export default interface Investor {
  id: string;
  contactId: string;
  fundOpportunityId?: string;
  status: 'CANDIDATE' | 'LEAD' | 'OPPORTUNITY' | 'COMMITTED_INVESTOR';
  eoiSubmitted?: boolean;
  eoiSubmittedOn?: Date | null;
  convertedToOpportunityOn?: Date | null;
  completedOn?: Date | null;
  indicativeInvestment?: number | null;
  preferredInvestment?: number | null;
  committedInvestment?: number | null;
  fundOpportunity?: FundOpportunity;
  investmentEntityId?: string;
  entityAllocations?: InvestmentEntityAllocation[];
  totalAllocatedAmount?: number;
  updatedAt: string;
  name?: string;
  email?: string;
  mobile?: string;
  userInitials?: string;
  contact?: Contact;
  suggestedIndicativeInvestment?: number | null;
  indicativeInvestmentSuggestedBy?: {
    id: string;
    name: string;
    email?: string;
    isCurrentAdvisor?: boolean;
  } | null;
  indicativeInvestmentSuggestedAt?: string | null;
  indicativeInvestmentSuggestedComment?: string | null;
  /** Persisted "last update viewed" from API (replaces localStorage for blue-dot logic). */
  lastUpdateViewedAt?: string | null;
}

export const investorToContactCardProps = (
  investor: FundInvestmentInvestor | Investor,
  baseTone: keyof typeof Palette,
  onPromote?: () => void,
  onDemote?: () => void,
  onDelete?: () => void,
) => ({
  name: investor.name ?? 'Unnamed',
  email: investor.email ?? '-',
  phone: investor.mobile ?? '-',
  investment: currentInvestment(investor),
  tone: baseTone,
  contactId: investor.contactId,
  onPromote,
  onDemote,
  onDelete,
  entity: investor,
});

const currentInvestment = (
  investor: Investor | FundInvestmentInvestor,
): number | null | undefined => {
  if (!investor) {
    return null;
  }

  if (investor.status == 'CANDIDATE') {
    return null;
  }

  if (investor.status == 'LEAD') {
    return investor.eoiSubmitted ? investor.indicativeInvestment! : null;
  }

  if (investor.status == 'OPPORTUNITY') {
    return investor.preferredInvestment!;
  }

  if (investor.status == 'COMMITTED_INVESTOR') {
    return investor.committedInvestment!;
  }
};

const currentInvestmentLabel = (investor: Investor) => {
  if (!investor) {
    return null;
  }

  if (investor.status == 'CANDIDATE') {
    return null;
  }

  if (investor.status == 'LEAD') {
    return investor.eoiSubmitted ? 'Proposed Investment' : null;
  }

  if (investor.status == 'OPPORTUNITY') {
    return 'Preferred Investment';
  }

  if (investor.status == 'COMMITTED_INVESTOR') {
    return 'Committed Investment';
  }
};

// Calculate remaining allocatable amount for investor
export const getRemainingAllocatableAmount = (investor: Investor): number => {
  const committedAmount = investor.committedInvestment || 0;
  const totalAllocated = investor.totalAllocatedAmount || 0;
  return Math.max(0, committedAmount - totalAllocated);
};

// Check if investor has full allocation
export const hasFullAllocation = (investor: Investor): boolean => {
  return getRemainingAllocatableAmount(investor) === 0;
};

export { currentInvestment, currentInvestmentLabel };
